/**
 * Translation Provider - React Context for i18n
 *
 * Provides language state and a synchronous t() function backed by
 * pre-built JSON locale files. Zero async, zero flicker.
 *
 * @security No sensitive data should be translated
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import { translateText, SUPPORTED_LANGUAGES, type LanguageCode } from './translate';

interface SetLanguageResult {
  success: boolean;
  error?: string;
}

interface TranslationContextValue {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => Promise<SetLanguageResult>;
  t: (text: string) => string;
  registerStrings: (strings: string[]) => void;
  isTranslating: boolean;
  languages: typeof SUPPORTED_LANGUAGES;
}

const TranslationContext = createContext<TranslationContextValue | null>(null);

interface TranslationProviderProps {
  children: React.ReactNode;
  defaultLanguage?: LanguageCode;
}

export function TranslationProvider({
  children,
  defaultLanguage = 'en'
}: TranslationProviderProps) {
  const { profile } = useAuth();
  const [language, setLanguageState] = useState<LanguageCode>(defaultLanguage);

  useEffect(() => {
    const profileLang = (profile as any)?.preferred_language;
    if (profileLang && SUPPORTED_LANGUAGES.some(l => l.code === profileLang)) {
      setLanguageState(profileLang as LanguageCode);
    }
  }, [profile]);

  const setLanguage = useCallback(async (lang: LanguageCode): Promise<SetLanguageResult> => {
    setLanguageState(lang);
    if (profile?.id) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ preferred_language: lang } as any)
          .eq('id', profile.id);

        if (error) {
          console.error('Failed to save language preference:', error.message);
          return { success: false, error: error.message };
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Network error';
        console.error('Failed to save language preference:', msg);
        return { success: false, error: msg };
      }
    }
    return { success: true };
  }, [profile?.id]);

  const t = useCallback((text: string): string => {
    return translateText(text, language);
  }, [language]);

  const registerStrings = useCallback((_strings: string[]) => {
    // No-op — static locale files don't need runtime registration
  }, []);

  const value: TranslationContextValue = {
    language,
    setLanguage,
    t,
    registerStrings,
    isTranslating: false,
    languages: SUPPORTED_LANGUAGES,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation(): TranslationContextValue {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}

/**
 * Pre-translate strings on component mount.
 * No-op with static locale files — kept for backward compatibility.
 */
export function usePreTranslate(_strings: string[]) {
  // No-op — translations are synchronous from static files
}
